import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { isEmpty, omit, pick } from 'lodash';

import ObjectRender from '@common/screens/RenderScreen/ObjectRender';
import { BaseComp, ComponentType } from '@common/types/element';
import { getActions } from '@common/utils/handleActions/func/helps';
import { getShadow } from '../func';
import { componentName } from '@common/constants/shared';

type Props = {
  children: BaseComp[];
  style: ViewStyle;
  width: number;
  height: number;
  marginTop: number;
  marginLeft: number;
  componentName: string;
  screenUuid: string;
  id: string;
  x: number;
  y: number;
  isInCustomList: boolean;
  currentListId?: string;
  currentRecordIds?: Record<string, any>;
  currentListIds?: Record<string, any>;
  selectedItem?: Record<string, any>;
  record?: Record<string, any>;
  initId: string;
  data: Record<string, any>;
  actions?: Record<string, any>;
  onPress: (action: any, options: any) => any;
};

const defaultStyle = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    display: 'flex',
  },
});

const containerStyles = [
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'transform',
  'width',
  'height',
];
const AutoLayout: React.FC<Props> = (props) => {
  const { children, style, screenUuid, id, x, y, isInCustomList, onPress } =
    props;

  if (style?.height === 'auto' && Platform.OS !== 'web') {
    delete style.height;
  }

  // Handle gap for AutoLayout
  const flexDirection = (style?.flexDirection as 'row' | 'column') || 'column';
  const gap = Number((style as any)?.gap || 0);

  // Apply gap properties for web only (use native CSS gap)
  // For React Native: we'll use margin on children instead
  const gapStyle: any = {};
  if (Platform.OS === 'web' && gap > 0) {
    gapStyle.gap = gap;
  }

  // Process children for AutoLayout
  let relativeChildCount = 0;
  const childrenWithoutPosition =
    children?.map((child) => {
      // Create a deep copy to avoid mutating props
      const childCopy = { ...child };

      // Only process relative positioned children for gap
      if (childCopy.style?.position === 'relative') {
        if (!childCopy.style?.left && childCopy.style?.left !== 0) {
          delete childCopy.style.left;
        }
        if (!childCopy.style?.top && childCopy.style?.top !== 0) {
          delete childCopy.style.top;
        }

        // React Native gap fallback: add margin to all relative children except the first
        if (Platform.OS !== 'web' && relativeChildCount > 0 && gap > 0) {
          if (flexDirection === 'column') {
            const existingMarginTop = Number(childCopy.style?.marginTop || 0);
            childCopy.style = {
              ...childCopy.style,
              marginTop: existingMarginTop + gap,
            };
          } else if (flexDirection === 'row') {
            const existingMarginLeft = Number(childCopy.style?.marginLeft || 0);
            childCopy.style = {
              ...childCopy.style,
              marginLeft: existingMarginLeft + gap,
            };
          }
        }

        relativeChildCount++;
      }
      // add screenUuid to child, this is the required parameter to run an action
      if (screenUuid) {
        childCopy.screenUuid = screenUuid;
      }
      return childCopy;
    }) || [];

  const childrenWithDatabinding: BaseComp[] = childrenWithoutPosition.map(
    (child) => {
      const selectedItem = {
        itemId: props.selectedItem?._id,
        tableId: props.selectedItem?.tableId,
        itemIndex: props.selectedItem?.itemIndex,
      };
      return {
        ...child,
        selectedItem,
        record: props.record,
        currentListIds: { ...(props.currentListIds || {}) },
        currentRecordIds: { ...(props.currentRecordIds || {}) },
        currentListId: props?.currentListId || props.id,
        ...(isInCustomList && {
          initId: `${child.id}`,
        }),
      };
    }
  );

  const handlePress = () => {
    onPress && onPress(getActions(props.actions), {});
  };

  const emptyAction = isEmpty(props.actions);

  const styles = {
    container: pick(style, containerStyles),
    flex: omit(style, containerStyles),
  };
  const zIndexStyle = () => {
    const hasSideBar = children.some(
      (child) =>
        child.type === ComponentType.libraryComponent &&
        (child.componentName === componentName.HEADER ||
          child.componentName === componentName.SIDEBAR)
    );
    return hasSideBar ? { zIndex: 9999 } : {};
  };

  return (
    <>
      {emptyAction ? (
        <View
          style={[
            defaultStyle.container,
            {
              ...style,
              ...gapStyle,
              ...getShadow(props),
            },
            zIndexStyle(),
          ]}
          nativeID={`render-auto-layout-${id}`}
        >
          <ObjectRender
            arrComp={childrenWithDatabinding}
            isScreen={false}
            layout={{
              offsetTop: y,
              offsetLeft: x,
            }}
            isAutoLayout
          />
        </View>
      ) : (
        <TouchableOpacity
          onPress={handlePress}
          style={[
            styles.container,
            {
              ...getShadow(props),
            },
            zIndexStyle(),
          ]}
        >
          <View
            style={[
              defaultStyle.container,
              {
                ...gapStyle,
                height: '100%',
              },
              styles.flex,
            ]}
            nativeID={`render-auto-layout-${id}`}
          >
            <ObjectRender
              arrComp={childrenWithDatabinding}
              isScreen={false}
              layout={{
                offsetTop: y,
                offsetLeft: x,
              }}
              isAutoLayout
            />
          </View>
        </TouchableOpacity>
      )}
    </>
  );
};

export default AutoLayout;
